/* =========================================================================
   cus-complete-the-build.js

   Builds the Your-Fit build summary, presentation-only (reads the rendered
   Globo DOM, writes a summary block — never touches form inputs or line-item
   properties).

   - Pro group (Fork / Seatpost / Setback / Comment) is hidden behind "Adjust";
     the summary lists its FULL current config (so the hidden config is visible).
   - Complete-the-Build upgrades are surfaced too, but only when CHANGED from
     stock (stock CtB is already visible in that section — no need to echo it).
   - Label flips "Stock setup" -> "Customized" when anything deviates from the
     value Globo pre-selected; changed values get a neon highlight.

   "Changed" = current selection differs from the BASELINE (the first non-null
   value Globo applies on load = the configured default). Robust for options
   whose label has no "(default X)" hint (e.g. Wheelset).
   ========================================================================= */
(function () {
  'use strict';

  var NAME_MAP = {
    'Seatpost Length': 'Seatpost', 'Seatpost Setback': 'Setback', 'Seatpost Offset': 'Offset',
    'Stem Length': 'Stem', 'Handlebar Length': 'Handlebar', 'Handlebar Width': 'Handlebar',
    'Bottom Bracket Type': 'BB type', 'Group Set': 'Groupset'
  };
  function shortName(text) {
    var n = (text || '').split('(')[0].replace(/[:*\s]+$/, '').trim();
    return NAME_MAP[n] || n;
  }
  function cleanValue(v) { return (v || '').replace(/\s*option\b/i, '').trim(); }
  function defaultFromLabel(text) { var m = (text || '').match(/\(default\s+([^)]+)\)/i); return m ? m[1].trim() : null; }

  function selectedValue(el) {            // the actual chosen value, or null
    var c = el.querySelector('input:checked');
    if (c) return c.value || null;
    var s = el.querySelector('select');
    if (s && s.selectedIndex > -1) {
      var o = s.options[s.selectedIndex];
      if (s.value && o && !/please select/i.test(o.text)) return s.value;
    }
    return null;
  }

  var baseline = new WeakMap();           // element -> first observed default

  function readItem(el, includeStock, tier) {
    // Respect Globo conditional logic: skip options it has hidden at the ELEMENT
    // level (e.g. Handlebar Length / Stem when "No Handle Bar" on a frameset).
    // The Pro group is hidden at the GROUP level (.ctb-collapsed), so its elements
    // keep their own display and are NOT skipped here.
    if (window.getComputedStyle(el).display === 'none') return null;
    var dependent = el.hasAttribute('data-has-clo');   // Globo marks conditionally-controlled fields
    var L = el.querySelector('.label-content');
    if (!L) return null;
    var ta = el.querySelector('textarea');
    if (ta) {                             // Comment
      var t = (ta.value || '').trim();
      if (!baseline.has(el)) baseline.set(el, '');
      var cchanged = t !== (baseline.get(el) || '');
      if (!t) return null;
      return { name: 'Comment', value: t.length > 32 ? t.slice(0, 32) + '…' : t, changed: cchanged, tier: tier, dependent: dependent };
    }
    var sel = selectedValue(el);
    if (!baseline.has(el) && sel !== null) baseline.set(el, sel);   // capture default once it lands
    var base = baseline.has(el) ? baseline.get(el) : null;
    var changed = base !== null && sel !== null && sel !== base;
    var display = sel || defaultFromLabel(L.textContent);
    if (!display) return null;
    if (!includeStock && !changed) return null;                    // (now both tiers show stock)
    return { name: shortName(L.textContent), value: cleanValue(display), changed: changed, tier: tier, dependent: dependent };
  }

  function collect(specs) {
    var items = [];
    specs.forEach(function (sp) {
      if (!sp.group) return;
      sp.group.querySelectorAll('.gpo-element').forEach(function (el) {
        var it = readItem(el, sp.includeStock, sp.tier);
        if (it) items.push(it);
      });
    });
    return items;
  }

  function childsOf(group) { return group.querySelector('.gpo-group__childs') || group; }

  function setupSummary(fitGroup, proGroup, ctbGroup) {
    if (!fitGroup) return null;
    if (proGroup) proGroup.classList.add('ctb-collapsed');

    var box = document.createElement('div'); box.className = 'ctb-stock-setup';
    var head = document.createElement('div'); head.className = 'ctb-stock-setup__head';
    var label = document.createElement('span'); label.className = 'ctb-stock-setup__label';
    var adjust = document.createElement('button');
    adjust.type = 'button'; adjust.className = 'ctb-adjust';
    adjust.setAttribute('aria-expanded', 'false'); adjust.textContent = 'Adjust';
    head.appendChild(label); head.appendChild(adjust);
    var list = document.createElement('ul'); list.className = 'ctb-stock-setup__list';
    box.appendChild(head); box.appendChild(list);
    childsOf(fitGroup).appendChild(box);

    if (proGroup) {
      adjust.addEventListener('click', function () {
        var open = proGroup.classList.toggle('ctb-collapsed') === false;
        adjust.setAttribute('aria-expanded', open ? 'true' : 'false');
        adjust.textContent = open ? 'Done' : 'Adjust';
      });
    } else { adjust.style.display = 'none'; }

    // pro = your fit config (bold); ctb = optional upgrades (muted, but still shown so
    // buyers see "what's included" — e.g. Power Meter: No Power Meter).
    var specs = [{ group: proGroup, includeStock: true, tier: 'fit' }, { group: ctbGroup, includeStock: true, tier: 'upgrade' }];
    function makeItemLi(it) {
      var li = document.createElement('li');
      var k = document.createElement('span'); k.className = 'ctb-k'; k.textContent = it.name + ':';
      var v = document.createElement('span');
      v.className = 'ctb-v' + (it.tier === 'upgrade' ? ' ctb-v--upgrade' : '') + (it.changed ? ' is-changed' : '');
      v.textContent = it.value;
      li.appendChild(k); li.appendChild(v);
      return li;
    }
    return function () {
      var items = collect(specs);
      var anyChanged = items.some(function (it) { return it.changed; });
      label.textContent = anyChanged ? 'Customized' : 'Stock setup';
      list.textContent = '';
      // Honor Globo's conditional logic (not hardcoded field names): a field Globo
      // flags as conditionally-controlled (data-has-clo -> item.dependent) nests under
      // the preceding non-dependent option it follows — its controller. So Handlebar/
      // Stem read as the Handle Bar's spec, BB Type under Bottom Bracket, etc., driven
      // by whatever conditionals the merchant set.
      var tree = [], lastParent = null;
      items.forEach(function (it) {
        if (it.dependent && lastParent) { (lastParent.children = lastParent.children || []).push(it); }
        else { tree.push(it); lastParent = it; }
      });
      tree.forEach(function (it) {
        var li = makeItemLi(it);
        if (it.children && it.children.length) {
          li.classList.add('ctb-has-children');
          var sub = document.createElement('span'); sub.className = 'ctb-children';
          it.children.forEach(function (c, i) {
            if (i) sub.appendChild(document.createTextNode(' · '));
            var ck = document.createElement('span'); ck.className = 'ctb-ck'; ck.textContent = c.name + ' ';
            var cv = document.createElement('span'); cv.className = 'ctb-cv' + (c.changed ? ' is-changed' : ''); cv.textContent = c.value;
            sub.appendChild(ck); sub.appendChild(cv);
          });
          li.appendChild(sub);
        }
        list.appendChild(li);
      });
      box.classList.toggle('ctb-customized', anyChanged);
    };
  }

  // --- Size guide: link "Size guide" by the Size option to THIS product's own
  //     sizing-chart image (each model has its own). Best-effort: render only if
  //     an image is found, otherwise silently do nothing. ---
  function findSizeChart() {
    // prefer the image inside the size-table section block (model-agnostic)
    var block = document.querySelector('[id*="size_table"], [id*="sizing"], [class*="size_table"]');
    if (block) { var im = block.querySelector('img'); if (im && (im.currentSrc || im.src)) return im.currentSrc || im.src; }
    // fallback: any image whose filename looks like a sizing chart/guide
    var imgs = document.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var s = imgs[i].currentSrc || imgs[i].src || '';
      if (/siz(e|ing)[-_ ]?(guide|chart|table)/i.test(s)) return s;
    }
    return null;
  }

  function setupSizeGuide(fitGroup) {
    var sizeEl = [].slice.call(fitGroup.querySelectorAll('.gpo-element')).filter(function (e) {
      var L = e.querySelector('.label-content'); return L && /^\s*size\s*$/i.test((L.textContent || '').trim());
    })[0];
    if (!sizeEl) return;
    var label = sizeEl.querySelector('.gpo-label') || sizeEl;
    if (!label || label.querySelector('.ctb-sizeguide')) return;
    var src = findSizeChart();
    if (!src) return;                                       // no chart for this model -> silent

    var btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'ctb-sizeguide'; btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M15.5 15.5L21 21" fill="none" stroke="currentColor" stroke-width="2"/></svg>Size guide';
    label.appendChild(btn);

    var zoom;   // hoisted; referenced by the error handler below
    var pop = document.createElement('div'); pop.className = 'ctb-sizeguide-pop'; pop.hidden = true;
    var bar = document.createElement('div'); bar.className = 'ctb-sizeguide-pop__bar'; bar.innerHTML = '<span>Sizing guide</span>';
    var x = document.createElement('button'); x.type = 'button'; x.className = 'ctb-sizeguide-pop__x'; x.setAttribute('aria-label', 'Close'); x.innerHTML = '&times;';
    bar.appendChild(x);
    var img = document.createElement('img'); img.className = 'ctb-sizeguide-pop__img'; img.src = src; img.alt = 'Sizing guide'; img.loading = 'lazy';
    img.addEventListener('error', function () { pop.remove(); if (zoom) zoom.remove(); btn.remove(); });   // truly best-effort
    var hint = document.createElement('div'); hint.className = 'ctb-sizeguide-pop__hint'; hint.textContent = 'Click chart to enlarge';
    pop.appendChild(bar); pop.appendChild(img); pop.appendChild(hint);
    document.body.appendChild(pop);

    // fullscreen zoom so the chart is actually readable (pinch-zooms on mobile)
    zoom = document.createElement('div'); zoom.className = 'ctb-sizeguide-zoom'; zoom.hidden = true;
    var zx = document.createElement('button'); zx.type = 'button'; zx.className = 'ctb-sizeguide-zoom__x'; zx.setAttribute('aria-label', 'Close'); zx.innerHTML = '&times;';
    var zimg = document.createElement('img'); zimg.src = src; zimg.alt = 'Sizing guide';
    zoom.appendChild(zx); zoom.appendChild(zimg);
    document.body.appendChild(zoom);

    var pinned = false, hideT;
    function place() {
      if (window.innerWidth < 1024) { pop.classList.add('is-sheet'); pop.style.top = pop.style.left = pop.style.maxWidth = ''; return; }
      pop.classList.remove('is-sheet');
      var r = btn.getBoundingClientRect();
      var w = Math.min(640, window.innerWidth - 24);
      pop.style.maxWidth = w + 'px';
      pop.style.top = Math.round(r.bottom + 8) + 'px';
      pop.style.left = Math.round(Math.max(8, Math.min(r.left, window.innerWidth - w - 12))) + 'px';
    }
    function show() { clearTimeout(hideT); place(); pop.hidden = false; btn.setAttribute('aria-expanded', 'true'); }
    function hide() { pop.hidden = true; pinned = false; btn.setAttribute('aria-expanded', 'false'); }
    btn.addEventListener('mouseenter', function () { if (!pinned && window.innerWidth >= 1024) show(); });
    btn.addEventListener('mouseleave', function () { if (!pinned) hideT = setTimeout(function () { if (!pop.matches(':hover')) hide(); }, 150); });
    pop.addEventListener('mouseenter', function () { clearTimeout(hideT); });
    pop.addEventListener('mouseleave', function () { if (!pinned && zoom.hidden) hide(); });
    btn.addEventListener('click', function (e) { e.preventDefault(); pinned = !pinned; pinned ? show() : hide(); });
    x.addEventListener('click', function (e) { e.stopPropagation(); hide(); });
    img.addEventListener('click', function () { zoom.hidden = false; });
    zoom.addEventListener('click', function (e) { if (e.target === zoom || e.target === zx) zoom.hidden = true; });
    document.addEventListener('click', function (e) { if (pinned && zoom.hidden && e.target !== btn && !btn.contains(e.target) && !pop.contains(e.target)) hide(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { if (!zoom.hidden) zoom.hidden = true; else hide(); } });
    window.addEventListener('resize', function () { if (!pop.hidden) place(); });
  }

  // --- Running total ("Selections will add $X to the price"): Globo lays this
  //     line ON a full-width divider rule and relies on the text having the
  //     store's opaque background to mask the rule behind it. On this configured
  //     layout that mask doesn't take, so the rule strikes THROUGH the text
  //     (visible on both mobile and desktop). Tag it so the CSS can drop the
  //     overlapping rule and re-present a clean summary; also reset any negative
  //     offset / stray <hr> that Globo used to pull the text onto the rule.
  //     Presentation-only: never touches inputs or the order payload. ---
  function normalizeTotal(app) {
    if (!app) return;
    var total = null, nodes = app.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (/to the price/i.test(n.textContent || '') &&
          ![].some.call(n.children, function (c) { return /to the price/i.test(c.textContent || ''); })) {
        total = n; break;
      }
    }
    if (!total || total.classList.contains('ctb-total')) return;   // idempotent: Globo re-renders on change
    total.classList.add('ctb-total');
    if (total.parentElement) total.parentElement.classList.add('ctb-total-wrap');
    var node = total;
    for (var d = 0; d < 3 && node && node !== app; d++) {          // undo the offsets that ride the text onto the rule
      var cs = window.getComputedStyle(node);
      if (parseFloat(cs.marginTop) < 0) node.style.setProperty('margin-top', '0', 'important');
      if (parseFloat(cs.marginBottom) < 0) node.style.setProperty('margin-bottom', '0', 'important');
      node = node.parentElement;
    }
    var prev = total.previousElementSibling;
    if (prev && prev.tagName === 'HR') prev.style.setProperty('display', 'none', 'important');
  }

  function init() {
    // Identify the 3 sections by ORDER (operator setup: Fit -> Pro -> Complete-the-build),
    // anchored on the universal "Size" option — no hardcoded add-on/handlebar names.
    var groups = [].slice.call(document.querySelectorAll('.gpo-group'));
    var fit = null, fi = -1;
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].querySelector('[data-field-name="Size"]')) { fit = groups[i]; fi = i; break; }
    }
    if (!fit) return false;                                 // Globo not rendered yet
    var pro = groups[fi + 1] || null;
    var ctb = groups[fi + 2] || null;
    // BACKWARDS-COMPAT: only activate when the product is actually grouped. An
    // ungrouped/legacy product has a single .gpo-group, so there is no distinct Pro
    // group -> do nothing, leaving Globo's flat layout completely untouched.
    if (!pro) return true;
    document.documentElement.classList.add('ctb-pdp');      // gates the (non-gpo) quantity tweak
    if (fit.parentElement) fit.parentElement.classList.add('ctb-active');   // gates the gpo CSS
    var app = fit.closest('.gpo-app') || fit.parentElement;   // Globo's app root (running total lives here)
    var render = setupSummary(fit, pro, ctb);
    if (render) render();
    setupSizeGuide(fit);                                    // best-effort; silent if no chart image
    normalizeTotal(app);
    ['change', 'input'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (e.target.closest && e.target.closest('.gpo-group')) { if (render) render(); normalizeTotal(app); }
      });
    });
    // Globo re-renders the total asynchronously (replacing the node), which drops
    // our class — re-tag it whenever the app subtree changes. normalizeTotal is a
    // no-op once tagged, so this can't loop.
    if (app && window.MutationObserver) {
      new MutationObserver(function () { normalizeTotal(app); }).observe(app, { childList: true, subtree: true });
    }
    return true;
  }

  // --- Add-on compatibility notes: merchant rules (theme settings -> JSON
  //     island, see snippets/qp-compat-rules-island.liquid) matched against
  //     SELECTED Globo option values. Display-only: reads controls, never
  //     writes them; renders in THEME-owned DOM (inside product-form, above
  //     the buy buttons) because Globo re-renders .gpo-app on every change.
  //     Free-text (Comment) can never trigger a note — selects/radios/checks
  //     only. Isolated init path: a failure here must not touch the grouped
  //     CTB summary/size-guide above. ---
  function readCompatRules() {
    var el = document.querySelector('script[data-qp-compat-rules]');
    if (!el) return [];
    try {
      var raw = JSON.parse(el.textContent) || [];
      var rules = [];
      for (var i = 0; i < raw.length; i++) {
        var kws = String(raw[i].keywords || '').toLowerCase().split(',')
          .map(function (k) { return k.trim(); })
          .filter(function (k) { return k.length; });
        if (kws.length && raw[i].message) {
          rules.push({ keywords: kws, heading: raw[i].heading || '', message: raw[i].message });
        }
      }
      return rules;
    } catch (e) { return []; }
  }

  function selectedGpoValues(app) {
    var out = [];
    [].forEach.call(app.querySelectorAll('select'), function (s) {
      if (s.value && String(s.value).trim()) out.push(s.value);
    });
    [].forEach.call(
      app.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'),
      function (i) { if (i.value) out.push(i.value); }
    );
    return out;
  }

  function setupCompatNotes() {
    var rules = readCompatRules();
    if (!rules.length) return true;                        // nothing configured -> done
    var section = document.querySelector('[data-section-type="product-page"]') || document;
    if (section.querySelector('.ctb-compat')) return true; // already set up (editor re-runs the script on section morphs)
    var app = section.querySelector('.gpo-app');
    // Globo injects .gpo-app INSIDE .m-product-form--wrapper (between quantity
    // and the ATC button), so anchor on the visible ATC button itself to land
    // below Globo's running total, directly above Add to Cart. The theme keeps
    // hidden duplicate ATC buttons (mobile/dynamic-checkout) -> pick the
    // rendered one. Wrapper fallback covers layouts without a matchable button.
    var anchor = null;
    var btns = section.querySelectorAll('.m-product-form--main button.m-add-to-cart');
    for (var b = 0; b < btns.length; b++) {
      if (btns[b].offsetParent !== null && btns[b].getBoundingClientRect().height > 0) { anchor = btns[b]; break; }
    }
    if (!anchor) anchor = section.querySelector('.m-product-form--main .m-product-form--wrapper');
    if (!app || !anchor) return false;                     // not rendered yet -> retry

    var box = document.createElement('div');
    box.className = 'ctb-compat is-empty';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    anchor.parentElement.insertBefore(box, anchor);

    var last = null;
    function render() {
      var values = selectedGpoValues(app).map(function (v) {
        return String(v).toLowerCase().trim();
      });
      var hits = [];
      for (var i = 0; i < rules.length; i++) {
        var r = rules[i], hit = false;
        for (var k = 0; k < r.keywords.length && !hit; k++) {
          for (var v = 0; v < values.length && !hit; v++) {
            if (values[v].indexOf(r.keywords[k]) !== -1) hit = true;
          }
        }
        if (hit) hits.push(r);
      }
      var key = hits.map(function (r) { return r.heading + '|' + r.message; }).join('~');
      if (key === last) return;                            // no aria-live spam
      last = key;
      box.innerHTML = '';
      hits.forEach(function (r) {
        var note = document.createElement('div');
        note.className = 'ctb-compat__note';
        if (r.heading) {
          var eb = document.createElement('div');
          eb.className = 'ctb-compat__eyebrow';
          eb.textContent = r.heading;                      // escaped
          note.appendChild(eb);
        }
        var body = document.createElement('div');
        body.className = 'ctb-compat__body';
        body.innerHTML = r.message;                        // merchant richtext (trusted theme setting)
        note.appendChild(body);
        box.appendChild(note);
      });
      box.classList.toggle('is-empty', hits.length === 0);
    }

    render();
    ['change', 'input'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (e.target && e.target.closest && e.target.closest('.gpo-app')) render();
      });
    });
    return true;
  }

  (function initCompatNotes() {
    var done = false, ct = 0;
    function attempt() {
      if (done) return true;
      try { done = setupCompatNotes(); }
      catch (e) { done = true; }                           // fail silent, stop retrying
      return done;
    }
    if (!attempt()) {
      var civ = setInterval(function () { if (attempt() || ++ct > 40) clearInterval(civ); }, 500);
    }
  })();

  var tries = 0;
  var iv = setInterval(function () { if (init() || ++tries > 40) clearInterval(iv); }, 500);
})();
